import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import {
  CalidadCodigoRes,
  ConteosTablero,
  EstadoConexion,
  EstadoProyectoCc,
  FiltroCalidadCodigo,
  ProyectoResumen,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CalidadCodigoService } from "../calidad-codigo.service";
import { ESTADO_PROYECTO, EstadoCc, FILTROS_TABLERO, credencialRechazada, estadoDe, textoDeError } from "../cc-estados";
import {
  Medida,
  SIN_DATO,
  conteoCriterios,
  diasEnTexto,
  entero,
  esHoy,
  fechaCorta,
  fechaHora,
  haceCuanto,
  hora,
  letra,
  nombreMetrica,
  numero,
  pct,
  textoCriterios,
} from "../cc-formato";
import { ajustePaginado, cuentaEstable, numerosDePagina, textoRango } from "../cc-paginado";
import { EstadoRegresoCc, RUTA_TABLERO, estadoDeRegreso } from "../cc-regreso";
import { FlotantesCcService } from "../flotantes/flotantes-cc.service";
import { TonoAnilloCc, tonoCobertura, tonoDuplicacion } from "../graficas/anillo-cc.component";
import { IconoCcComponent } from "../graficas/icono-cc.component";
import { EMITIENDO_PDF, avisoDeEmision } from "../reporte/actualizacion-cc";
import { descargarPdfProyecto } from "../reporte/pdf-proyecto";

/**
 * Qué se enseña:
 *   CARGANDO        la primera consulta (después, la lista se atenúa sin parpadear);
 *   DATOS           la lista de proyectos;
 *   SIN_RESULTADOS  hay proyectos pero el filtro o la búsqueda no dejan ninguno («Quitar filtros»);
 *   VACIO           todavía no hay ninguna foto guardada (primera vez);
 *   SIN_CREDENCIAL  sin token configurado y sin nada guardado;
 *   ERROR           sin sesión, sin permiso o sin conexión con el servidor del COS.
 */
type EstadoTablero = "CARGANDO" | "DATOS" | "SIN_RESULTADOS" | "VACIO" | "SIN_CREDENCIAL" | "ERROR";

/**
 * Una métrica del proyecto: código general, código nuevo y las condiciones
 * del Quality Gate que incumple. La tabla y el panel comparten estos datos.
 */
interface Cifra {
  rotulo: string;
  /** Lo que acompaña a la cifra grande: la letra A–E, el anillo de un porcentaje o nada. */
  icono: "letra" | "anillo" | null;
  letra: string;
  letraClase: string;
  /** 0..100 para el anillo; nulo sin dato. */
  anillo: number | null;
  /** El color del anillo: la cobertura y la duplicación por su porcentaje (verde, amarillo o rojo). */
  tonoAnillo: TonoAnilloCc;
  /** La cobertura y la duplicación tiñen también la pista de su anillo, tenue (se ve el color aunque el arco sea mínimo). */
  pistaConTono: boolean;
  /** El código general, abreviado como SonarQube («2.7 mil», «99.4 %»). */
  valor: string;
  /** El globo de la cifra: el valor completo («2,734 hallazgos abiertos») o el criterio que no se cumple. */
  titulo: string;
  /** La cifra general es criterio del gate y falla (en rojo sobrio). */
  falla: boolean;
  /** «nuevo», «nuevos» o vacío (cuando el texto ya lo dice: «sin código nuevo»). */
  nuevoPrefijo: string;
  /** La letra del código nuevo (seguridad, confiabilidad, mantenibilidad y hotspots); vacía en las demás. */
  nuevoLetra: string;
  nuevoLetraClase: string;
  nuevo: string;
  /** El globo del código nuevo: el criterio que no se cumple; vacío si cumple. */
  tituloNuevo: string;
  /** La cifra del código nuevo es criterio del gate y falla. */
  fallaNuevo: boolean;
  /**
   * Los criterios del gate que no cumple la cifra (general, nuevo o los dos), con el mismo texto de su globo: lo dice
   * el punto rojo junto al rótulo, uno por cifra, en su globo y a los lectores de pantalla. Vacío si cumple.
   */
  motivo: string;
}

/** Una marca junto a la fecha: en proceso (azul), falló (rojo) o desactualizado (ámbar). */
interface ChipFecha {
  clase: string;
  icono: string;
  texto: string;
  titulo: string;
}

/** Un renglón de la lista, armado UNA vez por respuesta. */
interface FilaTablero {
  id: number;
  nombre: string;
  etiqueta: string;
  gate: string;
  estado: EstadoCc<EstadoProyectoCc>;
  criteriosTexto: string;
  gateCambio: boolean;
  sinAnalisis: boolean;
  fecha: string;
  hace: string;
  chip: ChipFecha | null;
  /** «38 mil» (líneas de código); vacío sin dato. */
  lineas: string;
  lineasTitulo: string;
  /** El último análisis no trae líneas nuevas: se dice una vez en el renglón de datos y las cifras van sin código nuevo. */
  sinCodigoNuevo: boolean;
  cifras: Cifra[];
  notaClasica: string;
  enlaceSonar: string;
  crudo: ProyectoResumen;
}

/** Las métricas de cada cifra cuyo criterio del gate, si falla, la marca; las del código nuevo son las mismas con new_. */
const METRICAS_CIFRA = {
  seguridad: ["software_quality_security_rating", "security_rating", "software_quality_security_issues", "vulnerabilities"],
  confiabilidad: ["software_quality_reliability_rating", "reliability_rating", "software_quality_reliability_issues", "bugs"],
  mantenibilidad: ["software_quality_maintainability_rating", "sqale_rating", "software_quality_maintainability_issues", "code_smells"],
  hotspots: ["security_hotspots_reviewed", "security_review_rating"],
  cobertura: ["coverage", "line_coverage", "branch_coverage"],
  duplicacion: ["duplicated_lines_density"],
  hallazgos: ["violations"],
  aceptados: ["accepted_issues"],
} as const;

type ClaveCifra = keyof typeof METRICAS_CIFRA;

/** Las tres calificaciones: su letra del panel (MQR), sus hallazgos abiertos y su nombre. */
const CALIFICACIONES = [
  { clave: "seguridad", nuevo: "seguridadNuevo", rotulo: "Seguridad", hallazgos: "software_quality_security_issues", hallazgosNuevos: "new_software_quality_security_issues" },
  { clave: "confiabilidad", nuevo: "confiabilidadNuevo", rotulo: "Confiabilidad", hallazgos: "software_quality_reliability_issues", hallazgosNuevos: "new_software_quality_reliability_issues" },
  { clave: "mantenibilidad", nuevo: "mantenibilidadNuevo", rotulo: "Mantenibilidad", hallazgos: "software_quality_maintainability_issues", hallazgosNuevos: "new_software_quality_maintainability_issues" },
] as const;

const FILTROS = new Set<FiltroCalidadCodigo>(FILTROS_TABLERO.map((f) => f.valor));
const RUTA_PROYECTO = "/admin/calidad-codigo/proyecto";
/** Renglones por página: los que caben en la ventana, a lo más 12. */
const RENGLONES_MAX = 12;
/** El aire bajo la lista (relleno de la página y borde), en píxeles, para calcular los renglones que caben. */
const MARGEN_INFERIOR = 30;
const ESPERA_BUSQUEDA_MS = 300;
const ALTO_RENGLON = 76;
const UN_DECIMAL_O_NINGUNO = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 });

/**
 * Calidad de código · Tablero (docs/CALIDAD_CODIGO.md §5): la entrada del
 * módulo. La tabla compara los proyectos y selecciona uno para consultar sus
 * ocho métricas, el código nuevo y sus alertas en el panel lateral. Desde el
 * panel se abre el detalle completo, SonarQube o su PDF. «Actualizar» pide una
 * pasada de sincronización sin perder la selección. El reporte para QA se pide
 * en la página Reportes («Nuevo reporte para QA»).
 *
 * Solo lectura sobre la foto que guarda el COS: el navegador nunca habla
 * con SonarQube. El servidor ya filtra, ordena (no pasa, pasa, sin
 * análisis) y resuelve el estado, los criterios que fallan y las letras;
 * aquí solo se pinta. Los renglones por página se calculan con la altura de
 * la ventana; en pantallas pequeñas el detalle queda debajo de la tabla.
 *
 * Base visual: toolbar, estados vacíos y paginado de table-page
 * (src/styles.css), con los tokens del módulo
 * (calidad-codigo.shared.css y tablero-cc.component.css).
 */
@Component({
  selector: "app-tablero-cc",
  standalone: true,
  templateUrl: "./tablero-cc.component.html",
  styleUrls: ["../calidad-codigo.shared.css", "./tablero-cc.component.css"],
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, IconoCcComponent],
})
export class TableroCcComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly filtros = FILTROS_TABLERO;
  readonly rutaProyecto = RUTA_PROYECTO;
  @ViewChild("lista") private readonly lista?: ElementRef<HTMLElement>;

  estado: EstadoTablero = "CARGANDO";
  /** Mientras se vuelve a consultar: la lista se queda atenuada. */
  consultando = false;
  error = "";
  filtro: FiltroCalidadCodigo = "TODOS";
  busqueda = "";
  gate = "";
  gates: string[] = [];
  conteos: ConteosTablero | null = null;
  conexion: EstadoConexion | null = null;
  filas: FilaTablero[] = [];
  pagina = 1;
  porPagina = 8;
  /** Aire adicional de cada fila, compartido con el paginado de las tablas del módulo. */
  extraFila = 0;
  seleccionadoId: number | null = null;
  puedeConfigurar = false;
  actualizando = false;
  /** El id del proyecto cuyo PDF se está emitiendo. */
  emitiendo: number | null = null;

  private consulta?: Subscription;
  /** «Actualizar» y el PDF de un proyecto van cada uno por su lado: uno no cancela al otro. */
  private accionActualizar?: Subscription;
  private accionPdf?: Subscription;
  private esperaBusqueda: ReturnType<typeof setTimeout> | null = null;
  private esperaAjuste: ReturnType<typeof setTimeout> | null = null;
  private readonly ahora = new Date();
  /** El último state armado para abrir el detalle; se rehace solo cuando cambia la URL del tablero. */
  private ultimaVuelta: EstadoRegresoCc = estadoDeRegreso(RUTA_TABLERO);
  /** Ya se dejó la pantalla: lo que llegue tarde (las letras, una espera) no vuelve a medir. */
  private destruido = false;

  constructor(
    private readonly servicio: CalidadCodigoService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly flotantes: FlotantesCcService,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const filtro = (params.get("filtro") ?? "").toUpperCase() as FiltroCalidadCodigo;
    this.filtro = FILTROS.has(filtro) ? filtro : "TODOS";
    this.busqueda = params.get("q") ?? "";
    this.gate = params.get("gate") ?? "";
    this.consultar();
  }

  ngAfterViewInit(): void {
    this.ajustaFilas();
    // Si las letras de la página llegan después de la primera medida, los renglones y la barra cambian de alto: se
    // vuelve a medir cuando ya están.
    void document.fonts?.ready.then(() => this.programaAjuste());
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.accionActualizar?.unsubscribe();
    this.accionPdf?.unsubscribe();
    if (this.esperaBusqueda) {
      clearTimeout(this.esperaBusqueda);
    }
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
  }

  /* ============================ Consulta ============================ */

  /** `conservaPagina`: Actualizar y la recarga después de un PDF se quedan en la página que se veía. */
  consultar(conservaPagina = false): void {
    this.consulta?.unsubscribe();
    this.consultando = true;
    this.error = "";
    if (this.filas.length === 0) {
      this.estado = "CARGANDO";
    }
    this.cdr.detectChanges();
    this.consulta = this.servicio.tablero(this.filtro, this.busqueda, this.gate || null).subscribe({
      next: (res) => {
        this.consultando = false;
        if (res?.codigoError) {
          this.estado = "ERROR";
          this.error = textoDeError(res, "consultar el tablero");
          this.filas = [];
          this.seleccionadoId = null;
        } else {
          this.aplica(res, conservaPagina);
        }
        this.cdr.detectChanges();
        this.programaAjuste();
      },
      error: () => {
        this.consultando = false;
        this.estado = "ERROR";
        this.error = "No se pudo consultar el tablero: sin conexión con el servidor del COS.";
        this.filas = [];
        this.seleccionadoId = null;
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: CalidadCodigoRes, conservaPagina: boolean): void {
    const seleccionadoAnterior = this.seleccionada?.id;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.conexion = res.conexion ?? null;
    this.conteos = res.conteos ?? null;
    this.gates = res.gates ?? [];
    this.filas = (res.proyectos ?? []).map((p) => this.fila(p));
    // Una sincronización puede cambiar el orden: seguir al proyecto, incluso si pasó a otra página.
    const indiceSeleccionado = conservaPagina ? this.filas.findIndex((f) => f.id === seleccionadoAnterior) : -1;
    if (indiceSeleccionado >= 0) {
      this.pagina = Math.floor(indiceSeleccionado / this.porPagina) + 1;
    } else if (conservaPagina) {
      this.pagina = Math.min(this.pagina, Math.max(1, this.paginas));
    } else {
      this.pagina = 1;
    }
    this.seleccionadoId = indiceSeleccionado >= 0 ? seleccionadoAnterior! : null;
    this.ajustaSeleccion();
    if (this.filas.length > 0) {
      this.estado = "DATOS";
    } else if ((this.conteos?.todos ?? 0) > 0) {
      this.estado = "SIN_RESULTADOS";
    } else {
      this.estado = this.conexion && !this.conexion.configurada ? "SIN_CREDENCIAL" : "VACIO";
    }
  }

  /* ============================ Filtros ============================ */

  cambiaFiltro(valor: FiltroCalidadCodigo): void {
    if (this.filtro === valor) {
      return;
    }
    this.filtro = valor;
    this.guardaEnUrl();
    this.consultar();
  }

  /** La búsqueda espera a que se deje de escribir (300 ms) antes de consultar. */
  cambiaBusqueda(): void {
    if (this.esperaBusqueda) {
      clearTimeout(this.esperaBusqueda);
    }
    this.esperaBusqueda = setTimeout(() => {
      this.esperaBusqueda = null;
      this.guardaEnUrl();
      this.consultar();
    }, ESPERA_BUSQUEDA_MS);
  }

  cambiaGate(): void {
    this.guardaEnUrl();
    this.consultar();
  }

  quitarFiltros(): void {
    this.filtro = "TODOS";
    this.busqueda = "";
    this.gate = "";
    this.guardaEnUrl();
    this.consultar();
  }

  get hayFiltros(): boolean {
    return this.filtro !== "TODOS" || this.busqueda.trim() !== "" || this.gate !== "";
  }

  conteo(valor: FiltroCalidadCodigo): string {
    const c = this.conteos;
    if (!c) {
      return "";
    }
    switch (valor) {
      case "NO_PASA":
        return entero(c.noPasa);
      case "PASA":
        return entero(c.pasa);
      case "SIN_ANALISIS":
        return entero(c.sinAnalisis);
      case "DESACTUALIZADOS":
        return entero(c.desactualizados);
      default:
        return entero(c.todos);
    }
  }

  /** Proyectos que pasan sobre todos los registrados, incluidos los que todavía no tienen análisis. */
  get porcentajePasa(): number {
    const total = this.conteos?.todos ?? 0;
    return total > 0 ? Math.min(100, Math.max(0, ((this.conteos?.pasa ?? 0) / total) * 100)) : 0;
  }

  get textoPorcentajePasa(): string {
    return (this.conteos?.todos ?? 0) > 0 ? `${UN_DECIMAL_O_NINGUNO.format(this.porcentajePasa)} %` : SIN_DATO;
  }

  /** replaceUrl: cambiar un filtro no llena el historial del navegador. */
  private guardaEnUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        filtro: this.filtro === "TODOS" ? null : this.filtro,
        q: this.busqueda.trim() || null,
        gate: this.gate || null,
      },
      replaceUrl: true,
    });
  }

  /* ============================ Actualizar ============================ */

  /** Describe la última sincronización guardada; no presupone una conexión en vivo con SonarQube. */
  get estadoSincronizacion(): { texto: string; clase: string; icono: string } {
    if (this.actualizando || this.conexion?.enCurso || this.conexion?.ultimaSyncEstado === "EN_CURSO") {
      return { texto: "Sincronizando", clase: "is-info", icono: "sync" };
    }
    if (this.estado === "ERROR") {
      return { texto: "No se pudo consultar el tablero", clase: "is-no-pasa", icono: "cloud_off" };
    }
    if (this.consultando && !this.conexion && this.filas.length === 0) {
      return { texto: "Consultando datos", clase: "is-info", icono: "sync" };
    }
    if (this.conexion?.configurada && this.conexion.ultimaSyncEstado === "OK") {
      return { texto: "Última sincronización correcta", clase: "is-pasa", icono: "check_circle" };
    }
    if (this.conexion?.configurada && this.conexion.ultimaSyncEstado === "PARCIAL") {
      return { texto: "Última sincronización parcial", clase: "is-aviso", icono: "error_outline" };
    }
    if (this.conexion?.ultimaSyncEstado === "ERROR" || this.conexion?.configurada === false) {
      return { texto: this.filas.length || (this.conteos?.todos ?? 0) > 0 ? "Datos guardados" : "Sin sincronizar", clase: "is-aviso", icono: "cloud_off" };
    }
    return { texto: this.filas.length || (this.conteos?.todos ?? 0) > 0 ? "Datos guardados" : "Sin sincronizar", clase: "is-neutro", icono: "schedule" };
  }

  /**
   * La fecha de la cabecera y del globo de «Actualizar»: «Última consulta: hoy a
   * las 10:30», «Último intento: hoy a las
   * 10:42» (la pasada falló: ultimaSync es la hora del intento, no de la foto),
   * «Sincronizando desde las 10:30» o «Actualizando…».
   */
  get tituloActualizar(): string {
    if (this.actualizando) {
      return "Actualizando…";
    }
    const c = this.conexion;
    if (!c?.ultimaSync) {
      return c && !c.configurada ? "Sin credencial configurada" : "Sin consultar todavía";
    }
    if (c.enCurso) {
      return `Sincronizando desde las ${hora(c.ultimaSync)}`;
    }
    if (c.ultimaSyncEstado === "ERROR") {
      return `Último intento: ${this.cuando(c.ultimaSync)}`;
    }
    return `Última consulta: ${this.cuando(c.ultimaSync)}`;
  }

  /** «Actualizar» solo cuando puede servir: con credencial, sin otra consulta en curso y con el tablero a la vista. */
  get puedeActualizar(): boolean {
    if (this.actualizando || this.consultando || this.estado === "ERROR" || this.estado === "CARGANDO") {
      return false;
    }
    return this.conexion?.configurada !== false;
  }

  /** "hoy a las 10:30" o "el 06/10/2026 a las 10:30". */
  private cuando(iso: string): string {
    return esHoy(iso, this.ahora) ? `hoy a las ${hora(iso)}` : `el ${fechaCorta(iso)} a las ${hora(iso)}`;
  }

  /** La franja de arriba: SonarQube no respondió, la credencial no sirve o no hay credencial. */
  get franjaConexion(): { clase: string; icono: string; titulo: string; texto: string; reintentar: boolean } | null {
    const c = this.conexion;
    if (!c || this.estado === "ERROR" || this.estado === "SIN_CREDENCIAL") {
      return null;
    }
    if (!c.configurada) {
      return {
        clase: "is-aviso",
        icono: "vpn_key_off",
        titulo: "Sin credencial de SonarQube configurada.",
        texto: "Se muestran los datos guardados; la sincronización no corre hasta que se configure cos.sonar.token en el servidor.",
        reintentar: false,
      };
    }
    if (c.ultimaSyncEstado !== "ERROR") {
      return null;
    }
    // La hora del intento va en la cabecera y el globo de «Actualizar»: aquí no se repite. La regla de
    // la credencial rechazada es la misma del aviso y del pie de un reporte (cc-estados.ts).
    const error = c.ultimaSyncError ?? "";
    if (credencialRechazada(error)) {
      return {
        clase: "is-peligro",
        icono: "vpn_key_off",
        titulo: "SonarQube rechazó la credencial del COS (HTTP 401).",
        texto: "Avisa a quien administra el servidor del COS para renovarla. Se muestra la última foto que guardó el COS.",
        reintentar: true,
      };
    }
    return {
      clase: "is-aviso",
      icono: "cloud_off",
      titulo: "No se pudo consultar SonarQube.",
      texto: `${error || "Sin respuesta del servidor"}. Se muestra la última foto que guardó el COS.`,
      reintentar: true,
    };
  }

  /* ============================ Acciones ============================ */

  /** «Actualizar»: una pasada de sincronización de los 12 proyectos y luego se vuelve a consultar. */
  actualizar(): void {
    if (this.actualizando) {
      return;
    }
    this.actualizando = true;
    this.cdr.detectChanges();
    this.accionActualizar = this.servicio.actualizar().subscribe({
      next: (res) => {
        this.actualizando = false;
        const s = res?.sincronizacion;
        if (res?.codigoError) {
          this.flotantes.error(textoDeError(res, "actualizar"));
        } else if (s?.estado === "EN_CURSO") {
          this.flotantes.info(`Ya hay una sincronización en curso desde las ${hora(s.inicio)}; en un momento se reflejará aquí.`);
        } else if (s?.estado === "ERROR") {
          this.flotantes.error(`La sincronización no pudo con SonarQube: ${s.error || "sin respuesta"}.`);
        } else if (s) {
          this.flotantes.exito(`Actualizado: ${entero(s.proyectos)} proyectos, ${entero(s.analisisNuevos)} análisis nuevos y ${entero(s.cambios)} cambios en ${entero(s.llamadas)} llamadas.`);
        }
        this.consultar(true);
      },
      error: () => {
        this.actualizando = false;
        this.flotantes.error("No se pudo actualizar: sin conexión con el servidor del COS.");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * El PDF de un proyecto (el del renglón: viaja su id, con cualquier filtro, búsqueda o página): el servidor
   * consulta SonarQube, congela la foto con folio y aquí se arma con jsPDF. Uno a la vez: mientras sale, su botón
   * dice que emite (aria-disabled, con el foco donde estaba, y el anillo que gira) y los demás se apagan. Al
   * terminar, el flotante dice cómo le fue a la consulta (actualizacion-cc.ts); si trajo análisis nuevos o cambios,
   * o si la foto ya no dice lo mismo que el renglón (otra pasada guardó algo después de cargar la lista), la lista se
   * vuelve a pedir, en la misma página, para que diga lo mismo que el PDF. Si no, la conexión que manda la respuesta
   * pone al día el globo de «Actualizar» y la franja (que puede aparecer o irse: se vuelve a medir).
   */
  pdf(f: FilaTablero): void {
    if (this.emitiendo != null) {
      return;
    }
    this.emitiendo = f.id;
    this.cdr.detectChanges();
    this.accionPdf = this.servicio.reporteProyecto(f.id).subscribe({
      next: (res) => {
        this.emitiendo = null;
        if (res?.codigoError || !res?.reporte) {
          this.flotantes.error(textoDeError(res, "emitir el reporte"));
          this.cdr.detectChanges();
          return;
        }
        descargarPdfProyecto(res.reporte);
        // El renglón como está a la vista ahora («Actualizar» pudo rehacer la lista mientras salía el PDF).
        const enPantalla = this.filas.find((x) => x.id === f.id)?.crudo;
        const aviso = avisoDeEmision(`Reporte ${res.reporte.folio} de ${f.nombre}`, res.reporte, "PDF", enPantalla);
        this.flotantes[aviso.tipo](aviso.texto);
        if (aviso.recargar) {
          this.consultar(true);
          return;
        }
        this.conexion = res.conexion ?? this.conexion;
        this.cdr.detectChanges();
        this.programaAjuste();
      },
      error: () => {
        this.emitiendo = null;
        this.flotantes.error("No se pudo emitir el reporte: sin conexión con el servidor del COS.");
        this.cdr.detectChanges();
      },
    });
  }

  /** El globo del PDF de un renglón: sin análisis no hay reporte; mientras emite, primero consulta SonarQube. */
  tituloPdf(f: FilaTablero): string {
    if (f.sinAnalisis) {
      return "Sin análisis: todavía no hay reporte";
    }
    return this.emitiendo === f.id ? EMITIENDO_PDF : "Reporte PDF del proyecto";
  }

  /**
   * Lo que viaja en el state al abrir un proyecto: la URL del tablero con sus
   * filtros, para que «Regresar» del detalle vuelva a ella (cc-regreso.ts).
   * Es el mismo objeto mientras la URL no cambie: lo lee el [state] del
   * enlace de cada fila en cada repintado.
   */
  get vuelta(): EstadoRegresoCc {
    const url = this.router.url;
    if (this.ultimaVuelta.volverA !== url) {
      this.ultimaVuelta = estadoDeRegreso(url);
    }
    return this.ultimaVuelta;
  }

  /** El enlace del panel abre el detalle completo y conserva los filtros al regresar. */
  abre(f: FilaTablero): void {
    this.router.navigate([RUTA_PROYECTO, f.id], { state: this.vuelta });
  }

  /**
   * Seleccionar una fila solo cambia el panel; sus enlaces mantienen su propia acción.
   */
  abreDesdeFila(ev: Event, f: FilaTablero): void {
    if (ev.target === ev.currentTarget) {
      this.selecciona(f);
    }
  }

  selecciona(f: FilaTablero): void {
    if (!this.consultando && this.filasVisibles.some((visible) => visible.id === f.id)) {
      this.seleccionadoId = f.id;
    }
  }

  get seleccionada(): FilaTablero | null {
    const visibles = this.filasVisibles;
    return visibles.find((f) => f.id === this.seleccionadoId) ?? visibles[0] ?? null;
  }

  private ajustaSeleccion(): void {
    this.seleccionadoId = this.seleccionada?.id ?? null;
  }

  /* ============================ Paginado ============================ */

  /** Los renglones de la página a la vista. */
  get filasVisibles(): FilaTablero[] {
    const inicio = (this.pagina - 1) * this.porPagina;
    return this.filas.slice(inicio, inicio + this.porPagina);
  }

  /** Algún renglón a la vista trae cifras: solo entonces va la leyenda «Cifra grande: código general». */
  get hayCifras(): boolean {
    return this.filasVisibles.some((f) => !f.sinAnalisis);
  }

  get paginas(): number {
    return this.numerosDePagina.length;
  }

  get numerosDePagina(): number[] {
    return numerosDePagina(this.filas.length, this.porPagina);
  }

  /** "Mostrando 1-8 de 12 proyectos" o "12 proyectos". */
  get textoRango(): string {
    return textoRango(this.filas.length, this.pagina, this.porPagina, "proyecto", "proyectos");
  }

  irA(pagina: number): void {
    if (this.consultando) {
      return;
    }
    this.pagina = Math.min(Math.max(1, pagina), this.paginas);
    this.seleccionadoId = this.filasVisibles[0]?.id ?? null;
    this.programaAjuste();
  }

  @HostListener("window:resize")
  alCambiarTamano(): void {
    this.programaAjuste();
  }

  private programaAjuste(): void {
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    if (this.destruido) {
      return;
    }
    this.esperaAjuste = setTimeout(() => {
      this.esperaAjuste = null;
      this.ajustaFilas();
    }, 60);
  }

  /**
   * Mide las filas reales de la tabla con el mismo ajuste del resto del módulo.
   * Al cambiar el tamaño conserva el proyecto seleccionado dentro de la página.
   * La segunda pasada permite medir el pie y las filas nuevas sin oscilar entre
   * dos cantidades cuando una fila ocupa más alto que las demás.
   */
  private ajustaFilas(pasadas = 2, anteriorEncogio = false): void {
    const lista = this.lista?.nativeElement;
    if (!lista || this.filas.length === 0) {
      return;
    }
    // En una ventana baja el inspector necesita desplazamiento; cuatro filas evitan una tabla casi vacía.
    const ajuste = ajustePaginado(lista, MARGEN_INFERIOR, 4, RENGLONES_MAX, ALTO_RENGLON, this.filas.length);
    const porPagina = cuentaEstable(this.porPagina, ajuste.porPagina, anteriorEncogio);
    const paginas = numerosDePagina(this.filas.length, porPagina).length;
    const elegido = this.filas.findIndex((f) => f.id === this.seleccionada?.id);
    const referencia = elegido < 0 ? (this.pagina - 1) * this.porPagina : elegido;
    const pagina = Math.min(paginas, Math.floor(referencia / porPagina) + 1);
    if (porPagina === this.porPagina && pagina === this.pagina && ajuste.extraFila === this.extraFila) {
      return;
    }
    const encogio = porPagina < this.porPagina;
    this.porPagina = porPagina;
    this.pagina = pagina;
    this.extraFila = ajuste.extraFila;
    this.ajustaSeleccion();
    this.cdr.detectChanges();
    if (pasadas > 0) {
      this.esperaAjuste = setTimeout(() => {
        this.esperaAjuste = null;
        this.ajustaFilas(pasadas - 1, encogio);
      }, 60);
    }
  }

  trackFila(_: number, f: FilaTablero): number {
    return f.id;
  }

  trackCifra(_: number, c: Cifra): string {
    return c.rotulo;
  }

  /* ============================ Renglones ============================ */

  private fila(p: ProyectoResumen): FilaTablero {
    const m = p.medidas ?? {};
    const sinAnalisis = p.estado === "SIN_ANALISIS";
    // Sin código nuevo no hay nada que evaluar en él: sus criterios no marcan ninguna cifra.
    const fallan = new Set((p.criteriosQueFallan ?? []).filter((x) => p.sinCodigoNuevo !== true || !x.startsWith("new_")));
    const lineas = numero(m["ncloc"]);
    return {
      id: p.id,
      nombre: p.nombre,
      etiqueta: p.etiqueta ?? "",
      gate: p.gateNombre ?? "",
      estado: estadoDe(ESTADO_PROYECTO, p.estado),
      criteriosTexto: sinAnalisis ? "" : this.textoCriterios(p),
      gateCambio: p.gateCambio === true,
      sinAnalisis,
      fecha: fechaHora(p.fechaAnalisis),
      hace: haceCuanto(p.fechaAnalisis, this.ahora),
      chip: this.chip(p),
      lineas: lineas == null ? "" : abreviado(lineas),
      lineasTitulo: lineas == null ? "" : `${entero(lineas)} líneas de código`,
      sinCodigoNuevo: !sinAnalisis && p.sinCodigoNuevo === true,
      cifras: sinAnalisis ? [] : cifrasDe(p, m, fallan),
      notaClasica: p.calificaciones?.notaClasica ?? "",
      enlaceSonar: p.enlaceSonar,
      crudo: p,
    };
  }

  /** "pasan 14 de 15 · 1 sin dato · 1 ignorada". */
  private textoCriterios(p: ProyectoResumen): string {
    const c = conteoCriterios(p.criterios);
    const partes = [textoCriterios(c)];
    if (c.sinDato > 0) {
      partes.push(`${c.sinDato} sin dato`);
    }
    if (c.ignoradas > 0) {
      partes.push(`${c.ignoradas} ${c.ignoradas === 1 ? "ignorada" : "ignoradas"}`);
    }
    return partes.join(" · ");
  }

  private chip(p: ProyectoResumen): ChipFecha | null {
    if (p.ceEstado === "EN_PROCESO") {
      return { clase: "is-info", icono: "hourglass_top", texto: "análisis en proceso", titulo: "SonarQube está procesando un análisis nuevo; las cifras son del anterior." };
    }
    if (p.ceEstado === "FALLIDO") {
      return { clase: "is-no-pasa", icono: "warning_amber", texto: "el último análisis falló", titulo: p.ceError || "SonarQube no pudo procesar el último análisis; las cifras son del anterior." };
    }
    if (p.desactualizado) {
      // La misma cuenta que «hace …» del detalle (reloj de quien mira); los días del servidor solo si no hay fecha.
      const hace = haceCuanto(p.fechaAnalisis, this.ahora) || `hace ${diasEnTexto(p.diasDesdeAnalisis)}`;
      return { clase: "is-aviso", icono: "schedule", texto: hace, titulo: "Lleva más días sin analizarse que el umbral configurado." };
    }
    return null;
  }
}

/**
 * Un conteo abreviado como lo hace SonarQube en su lista de proyectos (2.7k,
 * 17k), en español: "12", "2.7 mil", "17 mil", "1.2 millones". Un decimal
 * por debajo de diez mil o de diez millones; «—» sin dato.
 */
export function abreviado(valor: Medida): string {
  const n = numero(valor);
  if (n == null) {
    return SIN_DATO;
  }
  const a = Math.abs(n);
  if (a < 1000) {
    return entero(n);
  }
  const miles = a < 10_000 ? Math.round(n / 100) / 10 : Math.round(n / 1000);
  if (Math.abs(miles) < 1000) {
    return `${UN_DECIMAL_O_NINGUNO.format(miles)} mil`;
  }
  const millones = a < 10_000_000 ? Math.round(n / 100_000) / 10 : Math.round(n / 1_000_000);
  return `${UN_DECIMAL_O_NINGUNO.format(millones)} ${millones === 1 ? "millón" : "millones"}`;
}

/** Un conteo del código nuevo, que va en letra chica: exacto hasta 9,999 («1,924» es más corto que «1.9 mil») y abreviado desde ahí. */
function corto(valor: Medida): string {
  return Math.abs(numero(valor) ?? 0) < 10_000 ? entero(valor) : abreviado(valor);
}

/** Las ocho cifras del renglón, en el orden de SonarQube y luego las del COS. */
function cifrasDe(p: ProyectoResumen, m: Record<string, string>, fallan: Set<string>): Cifra[] {
  const cal = p.calificaciones ?? {};
  const cifras: Cifra[] = [
    ...CALIFICACIONES.map((c) =>
      conFallas(c.clave, fallan, false, {
        rotulo: c.rotulo,
        icono: "letra",
        ...conLetra(letra(cal[c.clave], "")),
        anillo: null,
        valor: abreviado(m[c.hallazgos]),
        titulo: `${c.rotulo} del código general: ${entero(m[c.hallazgos])} hallazgos abiertos`,
        nuevoPrefijo: "nuevo",
        ...conLetraNueva(letra(cal[c.nuevo], "")),
        nuevo: corto(m[c.hallazgosNuevos]),
        tituloNuevo: `${c.rotulo} del código nuevo: ${entero(m[c.hallazgosNuevos])} hallazgos`,
      }),
    ),
    cifraHotspots(m, fallan),
    cifraPct("Cobertura", "cobertura", "coverage", m, fallan),
    cifraPct("Duplicación", "duplicacion", "duplicated_lines_density", m, fallan),
    cifraConteo("Hallazgos", "hallazgos", "violations", "hallazgos abiertos", m, fallan),
    cifraConteo("Aceptados", "aceptados", "accepted_issues", "hallazgos aceptados", m, fallan),
  ];
  return p.sinCodigoNuevo ? cifras.map(sinNuevo) : cifras;
}

/** Sin código nuevo: ninguna cifra lo repite ni lo marca; el renglón de datos lo dice una vez («sin código nuevo»). */
function sinNuevo(c: Cifra): Cifra {
  return { ...c, nuevoPrefijo: "", nuevoLetra: "", nuevoLetraClase: "", nuevo: "", tituloNuevo: "" };
}

/** Hotspots revisados: la letra de la revisión de seguridad y el porcentaje; en el código nuevo, «sin nuevos» si no hay. */
function cifraHotspots(m: Record<string, string>, fallan: Set<string>): Cifra {
  const sinNuevos = (numero(m["new_security_hotspots"]) ?? 0) === 0 || numero(m["new_security_hotspots_reviewed"]) == null;
  return conFallas("hotspots", fallan, sinNuevos, {
    rotulo: "Hotspots revisados",
    icono: "letra",
    ...conLetra(letra(m["security_review_rating"], "")),
    anillo: null,
    valor: pct(m["security_hotspots_reviewed"]),
    titulo: `Hotspots del código general: ${entero(m["security_hotspots"], "0")} por revisar`,
    nuevoPrefijo: sinNuevos ? "" : "nuevo",
    ...(sinNuevos ? { nuevoLetra: "", nuevoLetraClase: "" } : conLetraNueva(letra(m["new_security_review_rating"], ""))),
    nuevo: sinNuevos ? "sin nuevos" : pct(m["new_security_hotspots_reviewed"]),
    tituloNuevo: `Hotspots nuevos: ${entero(m["new_security_hotspots"], "0")}`,
  });
}

/** Cobertura y duplicación: el anillo y el porcentaje, y el del código nuevo. */
function cifraPct(rotulo: string, clave: ClaveCifra, metrica: string, m: Record<string, string>, fallan: Set<string>): Cifra {
  const nuevo = numero(m[`new_${metrica}`]);
  return conFallas(clave, fallan, nuevo == null, {
    rotulo,
    icono: "anillo",
    letra: "",
    letraClase: "",
    anillo: numero(m[metrica]),
    valor: pct(m[metrica]),
    titulo: `${rotulo} del código general`,
    nuevoPrefijo: "nuevo",
    nuevoLetra: "",
    nuevoLetraClase: "",
    nuevo: pct(nuevo),
    tituloNuevo: `${rotulo} del código nuevo`,
  });
}

/** Hallazgos y aceptados: el total abreviado y, en el código nuevo, cuántos. */
function cifraConteo(rotulo: string, clave: ClaveCifra, metrica: string, que: string, m: Record<string, string>, fallan: Set<string>): Cifra {
  const nuevo = m[`new_${metrica}`];
  return conFallas(clave, fallan, false, {
    rotulo,
    icono: null,
    letra: "",
    letraClase: "",
    anillo: null,
    valor: abreviado(m[metrica]),
    titulo: `${entero(m[metrica])} ${que} en el código general`,
    nuevoPrefijo: "nuevos",
    nuevoLetra: "",
    nuevoLetraClase: "",
    nuevo: corto(nuevo),
    tituloNuevo: `${entero(nuevo)} ${que} en el código nuevo`,
  });
}

/**
 * Lo que falla: si un criterio del gate de esta cifra falla, en el código
 * general o en el nuevo (salvo `sinNuevo`: no hay código nuevo que evaluar), su
 * globo dice cuál después de lo que ya decía («198 por revisar · No cumple …»)
 * y el motivo del punto rojo junta los dos.
 */
function conFallas(clave: ClaveCifra, fallan: Set<string>, sinNuevo: boolean, c: Omit<Cifra, "falla" | "fallaNuevo" | "motivo" | "tonoAnillo" | "pistaConTono">): Cifra {
  const general = METRICAS_CIFRA[clave].find((x) => fallan.has(x));
  const nuevo = sinNuevo ? undefined : METRICAS_CIFRA[clave].map(deCodigoNuevo).find((x) => fallan.has(x));
  return {
    ...c,
    falla: general != null,
    fallaNuevo: nuevo != null,
    tonoAnillo: tonoDelAnillo(clave, c.anillo, general != null),
    pistaConTono: clave === "cobertura" || clave === "duplicacion",
    titulo: general == null ? c.titulo : `${c.titulo} · ${tituloFalla(general)}`,
    tituloNuevo: nuevo == null ? c.tituloNuevo : `${c.tituloNuevo} · ${tituloFalla(nuevo)}`,
    motivo: [general, nuevo].filter((x) => x != null).map(tituloFalla).join(" · "),
  };
}

/** El color del anillo: la cobertura y la duplicación van por su porcentaje (verde, amarillo o rojo). */
function tonoDelAnillo(clave: ClaveCifra, anillo: number | null, falla: boolean): TonoAnilloCc {
  if (clave === "cobertura") {
    return tonoCobertura(anillo);
  }
  if (clave === "duplicacion") {
    return tonoDuplicacion(anillo);
  }
  return falla ? "no-pasa" : "neutro";
}

/** La métrica del código nuevo: new_ y la clave, salvo la mantenibilidad clásica (new_maintainability_rating). */
function deCodigoNuevo(metrica: string): string {
  return metrica === "sqale_rating" ? "new_maintainability_rating" : `new_${metrica}`;
}

function conLetra(l: string): Pick<Cifra, "letra" | "letraClase"> {
  return { letra: l || "–", letraClase: claseLetra(l) };
}

function conLetraNueva(l: string): Pick<Cifra, "nuevoLetra" | "nuevoLetraClase"> {
  return { nuevoLetra: l || "–", nuevoLetraClase: claseLetra(l) };
}

/** La clase de la letra A..E; sin dato, neutra. */
function claseLetra(l: string): string {
  return /^[A-E]$/.test(l) ? `is-${l}` : "is-nd";
}

function tituloFalla(metrica: string): string {
  return `No cumple «${nombreMetrica(metrica)}», criterio del Quality Gate`;
}
